import { UserQuestion } from '../types/UserQuestion';

const API_URL = process.env.REACT_APP_API_URL || '/api';

export const UserQuestionService = {
  // Get all user questions
  getAllUserQuestions: async (): Promise<UserQuestion[]> => {
    try {
      const response = await fetch(`${API_URL}/user-questions`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(localStorage.getItem('token') && { 'Authorization': `Bearer ${localStorage.getItem('token')}` })
        },
        mode: 'cors'
      });
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Failed to fetch user questions:', error);
      throw error;
    }
  },
  
  // Get user questions by email
  getUserQuestionsByEmail: async (email: string): Promise<UserQuestion[]> => {
    try {
      const response = await fetch(`${API_URL}/user-questions/email/${encodeURIComponent(email)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(localStorage.getItem('token') && { 'Authorization': `Bearer ${localStorage.getItem('token')}` })
        },
        mode: 'cors'
      });
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error(`Failed to fetch user questions for email ${email}:`, error);
      throw error;
    }
  },
  
  // Get a single user question by ID
  getUserQuestionById: async (id: number): Promise<UserQuestion> => {
    try {
      const response = await fetch(`${API_URL}/user-questions/${id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(localStorage.getItem('token') && { 'Authorization': `Bearer ${localStorage.getItem('token')}` })
        },
        mode: 'cors'
      });
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error(`Failed to fetch user question with ID ${id}:`, error);
      throw error;
    }
  },
  
  // Create a new user question
  createUserQuestion: async (question: UserQuestion): Promise<UserQuestion> => {
    try {
      const response = await fetch(`${API_URL}/user-questions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(localStorage.getItem('token') && { 'Authorization': `Bearer ${localStorage.getItem('token')}` })
        },
        mode: 'cors',
        body: JSON.stringify(question)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Failed to create user question:', error);
      throw error;
    }
  },
  
  // Update an existing user question
  updateUserQuestion: async (id: number, question: UserQuestion): Promise<UserQuestion> => {
    try {
      const response = await fetch(`${API_URL}/user-questions/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(localStorage.getItem('token') && { 'Authorization': `Bearer ${localStorage.getItem('token')}` })
        },
        mode: 'cors',
        body: JSON.stringify(question)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error(`Failed to update user question with ID ${id}:`, error);
      throw error;
    }
  },
  
  // Delete a user question
  deleteUserQuestion: async (id: number): Promise<void> => {
    try {
      const response = await fetch(`${API_URL}/user-questions/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(localStorage.getItem('token') && { 'Authorization': `Bearer ${localStorage.getItem('token')}` })
        },
        mode: 'cors'
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error(`Failed to delete user question with ID ${id}:`, error);
      throw error;
    }
  }
};

export default UserQuestionService; 